import { useQuery } from "@apollo/client";
import type { ChangeEvent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import {
  GetActivitiesQuery,
  GetActivitiesQueryVariables,
  GetActivityTypesQuery,
} from "@/graphql/graphql";
import { compareActivityTypes } from "@/utils/activities";
import { TEXT_DEBOUNCE_MS } from "@/utils/consts";

import {
  activitiesQueryReducer,
  buildActivitiesVariables,
  initialActivitiesQueryState,
} from "./ActivitiesTable.activitiesQueryState";
import { GET_ACTIVITIES_QUERY, GET_ACTIVITY_TYPES_QUERY } from "./ActivitiesTable.queries";

type UseActivitiesListQueryOptions = {
  accountId?: number;
  /**
   * When true, skips `GetActivities` and `GetActivityTypes` (e.g. call-plan modal closed).
   */
  skip: boolean;
};

export const useActivitiesListQuery = ({ accountId, skip }: UseActivitiesListQueryOptions) => {
  const [queryState, dispatchQuery] = useReducer(
    activitiesQueryReducer,
    undefined,
    initialActivitiesQueryState
  );
  const [titleSearchInput, setTitleSearchInput] = useState("");
  const [contentSearchInput, setContentSearchInput] = useState("");
  const lastDebouncedTitleRef = useRef("");
  const lastDebouncedContentRef = useRef("");

  const {
    data: activityTypesData,
    loading: activityTypesLoading,
    error: activityTypesError,
  } = useQuery<GetActivityTypesQuery>(GET_ACTIVITY_TYPES_QUERY, {
    skip,
    fetchPolicy: "cache-and-network",
  });

  const activitiesVariables = useMemo(
    () => buildActivitiesVariables(queryState, accountId),
    [accountId, queryState]
  );

  const {
    data: activitiesData,
    loading: activitiesLoading,
    error: activitiesError,
  } = useQuery<GetActivitiesQuery, GetActivitiesQueryVariables>(GET_ACTIVITIES_QUERY, {
    variables: activitiesVariables,
    skip,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const activities = useMemo(
    () => activitiesData?.activities.activities ?? [],
    [activitiesData?.activities.activities]
  );
  const totalActivities = activitiesData?.activities.total ?? 0;

  const sortedActivityTypes = useMemo(() => {
    const types = activityTypesData?.activityTypes ?? [];
    return [...types].sort(compareActivityTypes);
  }, [activityTypesData?.activityTypes]);

  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = titleSearchInput.trim();
      if (trimmed === lastDebouncedTitleRef.current) {
        return;
      }

      lastDebouncedTitleRef.current = trimmed;
      dispatchQuery({ type: "SET_TITLE_SEARCH", titleSearch: trimmed });
    }, TEXT_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [titleSearchInput]);

  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = contentSearchInput.trim();
      if (trimmed === lastDebouncedContentRef.current) {
        return;
      }

      lastDebouncedContentRef.current = trimmed;
      dispatchQuery({ type: "SET_CONTENT_SEARCH", contentSearch: trimmed });
    }, TEXT_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [contentSearchInput]);

  const filtersReady = !!activityTypesData && !activityTypesLoading;

  const handlePageChange = useCallback(
    (_: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      dispatchQuery({ type: "SET_PAGE", page: newPage });
    },
    []
  );

  const handleRowsPerPageChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    dispatchQuery({ type: "SET_PAGE_SIZE", pageSize: newRowsPerPage });
  }, []);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        titleSearchInput.trim() ||
          queryState.titleSearch ||
          contentSearchInput.trim() ||
          queryState.contentSearch ||
          queryState.categories?.length ||
          queryState.typeKeys?.length ||
          queryState.difficulties?.length ||
          queryState.usage
      ),
    [
      contentSearchInput,
      queryState.categories?.length,
      queryState.contentSearch,
      queryState.difficulties?.length,
      queryState.titleSearch,
      queryState.typeKeys?.length,
      queryState.usage,
      titleSearchInput,
    ]
  );

  const handleClearAllFilters = useCallback(() => {
    lastDebouncedTitleRef.current = "";
    lastDebouncedContentRef.current = "";
    setTitleSearchInput("");
    setContentSearchInput("");
    dispatchQuery({ type: "CLEAR_FILTERS" });
  }, []);

  const typeKeysSelected = useMemo(
    () => Object.fromEntries((queryState.typeKeys || []).map((k) => [k, true])),
    [queryState.typeKeys]
  );

  const resetList = useCallback(() => {
    lastDebouncedTitleRef.current = "";
    lastDebouncedContentRef.current = "";
    setTitleSearchInput("");
    setContentSearchInput("");
    dispatchQuery({ type: "RESET" });
  }, []);

  return {
    queryState,
    dispatchQuery,
    titleSearchInput,
    setTitleSearchInput,
    contentSearchInput,
    setContentSearchInput,
    activitiesLoading,
    activities,
    totalActivities,
    sortedActivityTypes,
    filtersReady,
    hasActiveFilters,
    handleClearAllFilters,
    typeKeysSelected,
    handlePageChange,
    handleRowsPerPageChange,
    activitiesError,
    activityTypesError,
    resetList,
  };
};
